import { z } from 'zod';
import { perFieldTokensSchema } from '../bulk-ai/field-stats.js';

export const BULK_AI_EVENTS = {
  REQUESTED: 'bulk_ai.requested',
  STARTED: 'bulk_ai.started',
  PROGRESS: 'bulk_ai.progress',
  RUN_STARTED: 'bulk_ai.run.started',
  BATCH_STARTED: 'bulk_ai.batch.started',
  BATCH_DONE: 'bulk_ai.batch.done',
  BATCH_FAILED: 'bulk_ai.batch.failed',
  DONE: 'bulk_ai.done',
  FAILED: 'bulk_ai.failed',
} as const;

// Pairs items are kept as ONE flat object schema (not a discriminated union)
// so the parser stays lenient across entities — the `entity` field is what
// actually discriminates which sub-fields a given handler expects. Blog pairs
// only ever populate `post_id`; wallpaper pairs only ever populate
// `wallpaper_id`/`category_id`. All three fields are optional so neither
// shape is required by the other — existing wallpaper/category payloads
// (which always send wallpaper_id+category_id together) are unaffected.
export const bulkAiPairSchema = z.object({
  wallpaper_id: z.number().int().positive().optional(),
  category_id: z.number().int().positive().optional(),
  /** Blog entity only (T2/P4) — id of the source blog_posts row to translate. */
  post_id: z.number().int().positive().optional(),
});

export type BulkAiPair = z.infer<typeof bulkAiPairSchema>;

export const bulkAiRequestedPayloadSchema = z.object({
  entity: z.enum(['categories', 'wallpapers', 'blog']),
  operation: z.enum(['translate', 'generate']),
  source_lang: z.string().min(2).max(10).optional(),
  target_langs: z.array(z.string().min(2).max(10)).min(1),
  fields: z.array(z.string()).min(1),
  overwrite: z.boolean().default(false),
  category_ids: z.array(z.number().int().positive()).optional(),
  pairs: z.array(bulkAiPairSchema).optional(),
  scope_mode: z.enum(['by_confidence', 'default_floor', 'manual']).optional(),
  scope_confidence_floor: z.number().min(0).max(1).optional(),
  scope_manual_category_ids: z.array(z.number().int().positive()).optional(),
  /**
   * Generate input source. 'text' uses the wallpaper's step1 (describe)
   * output as the LLM input — cheaper, no vision call. 'vision' re-scans
   * the image. Only meaningful for wallpapers × generate; ignored otherwise.
   */
  vision_mode: z.enum(['text', 'vision']).optional(),
});

export type BulkAiRequestedPayload = z.infer<typeof bulkAiRequestedPayloadSchema>;

export const bulkAiBatchRowSchema = z.object({
  id: z.number().int().positive(),
  category_id: z.number().int().positive().optional(),
  uri_or_slug: z.string(),
  status: z.enum(['new', 'replaced', 'skip', 'fail']),
  fields: z.array(z.string()),
  error: z.string().optional(),
});

export type BulkAiBatchRow = z.infer<typeof bulkAiBatchRowSchema>;

export const bulkAiPlanItemSchema = z.object({
  id: z.number().int().positive(),
  category_id: z.number().int().positive().optional(),
  target_lang: z.string().min(2).max(10),
  fields: z.array(z.string()),
  action: z.enum(['new', 'replace', 'skip']),
  skip_reason: z.string().optional(),
});

export type BulkAiPlanItem = z.infer<typeof bulkAiPlanItemSchema>;

export const bulkAiRunStartedPayloadSchema = z.object({
  prompt_system: z.string(),
  prompt_user: z.string(),
  plan: z.array(bulkAiPlanItemSchema),
  model: z.string(),
});

export type BulkAiRunStartedPayload = z.infer<typeof bulkAiRunStartedPayloadSchema>;

export const bulkAiBatchStartedPayloadSchema = z.object({
  batch_n: z.number().int().positive(),
  target_lang: z.string().min(2).max(10),
  ids: z.array(z.union([z.number().int().positive(), z.string()])),
  preview_ids: z.array(z.union([z.number().int().positive(), z.string()])),
  category_ids: z.array(z.number().int().positive()).optional(),
});

export type BulkAiBatchStartedPayload = z.infer<typeof bulkAiBatchStartedPayloadSchema>;

export const bulkAiBatchDonePayloadSchema = z.object({
  batch_n: z.number().int().positive(),
  target_lang: z.string().min(2).max(10),
  ok_count: z.number().int().nonnegative(),
  fail_count: z.number().int().nonnegative(),
  skip_count: z.number().int().nonnegative(),
  tok_in: z.number().int().nonnegative(),
  tok_out: z.number().int().nonnegative(),
  cost: z.number().nonnegative(),
  cost_source: z.enum(['billed', 'estimate', 'none']).optional(),
  duration_ms: z.number().int().nonnegative(),
  rows: z.array(bulkAiBatchRowSchema),
  /** Raw items JSON sent to the LLM (Input tab in BatchDetail). */
  items_json: z.string().optional(),
  /** Raw model response (Output tab in BatchDetail). */
  raw_response: z.string().optional(),
  /**
   * AI model ID that was used for this batch. Optional for backward compatibility
   * with legacy in-flight events that lack it; stats-updater will skip-with-warn if absent.
   */
  model_id: z.number().int().positive().optional(),
  /** Per-field token attribution block — consumed by stats-updater handler. */
  per_field_tokens: z.array(perFieldTokensSchema).optional(),
  /** Prompt kind for this batch (e.g. 'translate_categories'). */
  kind: z.string().optional(),
  /** Execution mode: 'default' | 'text' | 'vision'. */
  mode: z.string().optional(),
});

export type BulkAiBatchDonePayload = z.infer<typeof bulkAiBatchDonePayloadSchema>;

export const bulkAiBatchFailedPayloadSchema = z.object({
  batch_n: z.number().int().positive(),
  target_lang: z.string().min(2).max(10),
  ok_count: z.number().int().nonnegative(),
  fail_count: z.number().int().nonnegative(),
  error_code: z.string(),
  error_message: z.string(),
  tok_in: z.number().int().nonnegative(),
  tok_out: z.number().int().nonnegative(),
  cost: z.number().nonnegative(),
  cost_source: z.enum(['billed', 'estimate', 'none']).optional(),
  duration_ms: z.number().int().nonnegative(),
  rows: z.array(bulkAiBatchRowSchema),
  items_json: z.string().optional(),
  raw_response: z.string().optional(),
  /**
   * AI model ID that was used for this batch. Optional for backward compatibility
   * with legacy in-flight events that lack it; stats-updater will skip-with-warn if absent.
   */
  model_id: z.number().int().positive().optional(),
});

export type BulkAiBatchFailedPayload = z.infer<typeof bulkAiBatchFailedPayloadSchema>;

export const bulkAiProgressPayloadSchema = z.object({
  total: z.number().int().nonnegative(),
  done: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
  skipped: z.number().int().nonnegative(),
  current_batch: z.number().int().nonnegative().optional(),
});

export type BulkAiProgressPayload = z.infer<typeof bulkAiProgressPayloadSchema>;

export const bulkAiStartedPayloadSchema = z.object({
  prompt_id: z.number().int().positive(),
  prompt_version: z.number().int().nonnegative(),
  model_label: z.string().nullable(),
  system_template: z.string(),
  user_template: z.string(),
  source_lang: z.string().min(2).max(10).nullable().optional(),
  target_langs: z.array(z.string().min(2).max(10)),
  fields: z.array(z.string()),
  overwrite: z.boolean(),
  total_source_rows: z.number().int().nonnegative(),
  items_to_translate: z.number().int().nonnegative(),
  items_skipped: z.number().int().nonnegative(),
  total_batches: z.number().int().nonnegative(),
  batch_size: z.number().int().nonnegative(),
  plan: z.array(bulkAiPlanItemSchema),
});

export type BulkAiStartedPayload = z.infer<typeof bulkAiStartedPayloadSchema>;

export const bulkAiDonePayloadSchema = z.object({
  total: z.number().int().nonnegative(),
  done: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
  skipped: z.number().int().nonnegative(),
  tokens_in: z.number().int().nonnegative(),
  tokens_out: z.number().int().nonnegative(),
  cost_usd: z.number().nonnegative(),
  cost_source: z.enum(['billed', 'estimate', 'none']).optional(),
  duration_ms: z.number().int().nonnegative(),
});

export type BulkAiDonePayload = z.infer<typeof bulkAiDonePayloadSchema>;

export const bulkAiFailedPayloadSchema = z.object({
  error: z.string(),
  partial: z.object({
    total: z.number().int().nonnegative(),
    done: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
    skipped: z.number().int().nonnegative(),
  }).optional(),
});

export type BulkAiFailedPayload = z.infer<typeof bulkAiFailedPayloadSchema>;
